import { z } from 'zod';

// VenueTypeEnum removed - venue types are now dynamic via VenueTypeConfig table

/**
 * Schema for a break period within operating hours
 */
export const TimeBreakSchema = z.object({
  start: z.string().regex(/^\d{2}:\d{2}$/),
  end: z.string().regex(/^\d{2}:\d{2}$/),
});

/**
 * Operating hours schema for a single day
 *
 * Supports three scenarios:
 * 1. Closed: { closed: true }
 * 2. 24/7: { is24Hours: true, open: "00:00", close: "23:59" }
 * 3. Regular: { open: "09:00", close: "17:00" }
 *    - Special case: close: "00:00" means midnight of next day (e.g., "12:00" to "00:00")
 */
const DayHoursSchema = z
  .object({
    open: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Time must be in HH:mm format')
      .optional(),
    close: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Time must be in HH:mm format')
      .optional(),
    closed: z.boolean().optional(),
    is24Hours: z.boolean().optional(),
    breaks: z.array(TimeBreakSchema).default([]),
  })
  .refine(
    (data) => {
      // If not provided or closed, skip validation
      if (!data || data.closed) return true;
      // If 24-hour operation, skip time validation
      if (data.is24Hours) return true;

      // If open or close are not provided (undefined), skip comparison validation
      if (!data.open || !data.close) return true;

      // Special case: "00:00" close time means end of day (next day midnight)
      // This is valid regardless of open time (e.g., "12:00" to "00:00" = noon to midnight)
      if (data.close === '00:00') return true;

      // Otherwise, validate open < close (same-day comparison)
      return data.open < data.close;
    },
    { message: 'Open time must be before close time' }
  )
  .refine(
    (data) => {
      // Skip break validation if closed or no breaks
      if (!data || data.closed) return true;
      if (!data.breaks || data.breaks.length === 0) return true;

      // Validate each break's start < end
      for (const brk of data.breaks) {
        if (brk.start >= brk.end) return false;
      }
      return true;
    },
    { message: 'Each break start time must be before its end time' }
  )
  .refine(
    (data) => {
      // Skip break validation if closed or no breaks
      if (!data || data.closed) return true;
      if (!data.breaks || data.breaks.length === 0) return true;

      // Validate breaks fall within the day's open/close window
      // Skip if open or close not provided
      if (!data.open || !data.close) return true;
      const effectiveClose = data.close === '00:00' ? '24:00' : data.close;
      for (const brk of data.breaks) {
        if (brk.start < data.open || brk.end > effectiveClose) return false;
      }
      return true;
    },
    { message: 'Breaks must fall within the operating hours window' }
  )
  .refine(
    (data) => {
      // Skip break validation if closed or fewer than 2 breaks
      if (!data || data.closed) return true;
      if (!data.breaks || data.breaks.length < 2) return true;

      // Sort breaks by start time and check for overlaps
      const sorted = [...data.breaks].sort((a, b) => a.start.localeCompare(b.start));
      for (let i = 0; i < sorted.length - 1; i++) {
        if (sorted[i].end > sorted[i + 1].start) return false;
      }
      return true;
    },
    { message: 'Breaks must not overlap' }
  )
  .optional();

/**
 * Base operating hours schema (internal - no business rule validation)
 * Used as foundation for both input and output schemas
 */
const BaseOperatingHoursSchema = z.object({
  monday: DayHoursSchema,
  tuesday: DayHoursSchema,
  wednesday: DayHoursSchema,
  thursday: DayHoursSchema,
  friday: DayHoursSchema,
  saturday: DayHoursSchema,
  sunday: DayHoursSchema,
});

/**
 * Operating hours schema for INPUT (CREATE/UPDATE operations)
 * Enforces business rule: at least one day must be open
 */
export const OperatingHoursSchema = BaseOperatingHoursSchema.refine(
  (hours) => {
    // At least one day must be open
    const days = Object.values(hours);
    return days.some((day) => day && !day.closed);
  },
  { message: 'At least one day must be open' }
);

export type OperatingHours = z.infer<typeof OperatingHoursSchema>;

/**
 * Operating hours schema for OUTPUT (API responses)
 * Accepts empty object or partial data for venues without hours configured
 * No business rule validation - allows venues with all days closed or no hours set
 */
export const OperatingHoursOutputSchema = z.union([BaseOperatingHoursSchema, z.object({})]);

export type OperatingHoursOutput = z.infer<typeof OperatingHoursOutputSchema>;

/**
 * Amenities schema
 */
export const AmenitiesSchema = z.object({
  wifi: z.boolean().optional(),
  parking: z.boolean().optional(),
  airConditioning: z.boolean().optional(),
  wheelchairAccessible: z.boolean().optional(),
  restrooms: z.boolean().optional(),
  foodAndBeverages: z.boolean().optional(),
  lockers: z.boolean().optional(),
  waitingArea: z.boolean().optional(),
  gaming: z.boolean().optional(),
});

export type Amenities = z.infer<typeof AmenitiesSchema>;

/**
 * Create venue schema (Admin only - assigns to vendor)
 * Used by: POST /venues (SUPER_ADMIN)
 */
export const CreateVenueSchema = z.object({
  vendorId: z.string().uuid('Invalid vendor ID'),
  name: z.string().min(3, 'Name must be at least 3 characters').max(100),
  description: z.string().min(20, 'Description must be at least 20 characters').max(2000),
  venueType: z.string().optional(), // Deprecated: derived from venueTypes[0] server-side
  venueTypes: z.array(z.string()).min(1, 'At least one category required'),

  // Location
  cityId: z.string().uuid('Invalid city ID'),
  stateId: z.string().uuid('Invalid state ID'),
  address: z.string().min(10, 'Address must be at least 10 characters').max(500),
  locality: z.string().max(100).optional(),
  landmark: z.string().max(200).optional(),
  pincode: z.string().regex(/^\d{6}$/, 'Pincode must be 6 digits'),
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  googleMapsLink: z
    .string()
    .url('Invalid URL format')
    .regex(
      /^https:\/\/(maps\.google\.com|goo\.gl\/maps|www\.google\.com\/maps|maps\.app\.goo\.gl)/,
      'Must be a Google Maps URL'
    )
    .optional()
    .nullable(),

  // Pricing
  basePrice: z.coerce
    .number({ invalid_type_error: 'Please enter a valid price' })
    .min(1, 'Base Price is required'),
  currency: z.string().default('INR'),
  maxCapacity: z.number().int().positive().optional(),

  // Contact
  phoneNumber: z
    .string()
    .regex(/^[6-9]\d{9}$/, 'Invalid phone number')
    .optional(),
  whatsappNumber: z
    .string()
    .regex(/^[6-9]\d{9}$/, 'Invalid WhatsApp number')
    .optional(),
  email: z.string().email('Invalid email').optional(),

  // Additional
  amenities: AmenitiesSchema.optional(),
  operatingHours: OperatingHoursSchema.optional(),
});

export type CreateVenueRequest = z.infer<typeof CreateVenueSchema>;

/**
 * Update venue schema
 * Used by: PUT /venues/:id
 */
export const UpdateVenueSchema = CreateVenueSchema.partial().extend({
  description: z.string().min(1).max(2000).optional(), // Less strict than create (min 1 vs min 20)
  cancellationPolicy: z
    .object({
      rules: z.array(
        z.object({
          hoursBeforeBooking: z.number().min(0),
          refundPercentage: z.number().min(0).max(100),
        })
      ),
    })
    .optional(),
});

export type UpdateVenueRequest = z.infer<typeof UpdateVenueSchema>;

/**
 * Venue filter schema for listing
 * Used by: GET /venues
 */
export const VenueFilterSchema = z.object({
  page: z.coerce.number().min(1).default(1),
  limit: z.coerce.number().min(1).max(100).default(20),
  venueType: z.string().optional(), // Now accepts any venue type code from VenueTypeConfig
  cityId: z.string().uuid().optional(),
  stateId: z.string().uuid().optional(),
  search: z.string().optional(),
  minPrice: z.coerce.number().optional(),
  maxPrice: z.coerce.number().optional(),
  isApproved: z.coerce.boolean().optional(),
  isActive: z.coerce.boolean().optional(),
  // Amenities filter - comma-separated list of amenity keys
  amenities: z.string().optional(), // e.g., "wifi,parking,airConditioning"
  // Location-based search
  latitude: z.coerce.number().min(-90).max(90).optional(),
  longitude: z.coerce.number().min(-180).max(180).optional(),
  radius: z.coerce.number().min(1).max(100).default(10).optional(), // km
});

export type VenueFilterQuery = z.infer<typeof VenueFilterSchema>;

/**
 * Venue approval schema
 * Used by: PUT /venues/:id/approve
 */
export const VenueApprovalSchema = z
  .object({
    approve: z.boolean(),
    reason: z.string().optional(),
  })
  .refine((data) => data.approve === true || (data.approve === false && data.reason), {
    message: 'Reason is required when rejecting a venue',
    path: ['reason'],
  });

export type VenueApprovalRequest = z.infer<typeof VenueApprovalSchema>;

/**
 * Venue status enum (5-state workflow)
 */
export const VenueStatusEnum = z.enum(['pending', 'approved', 'active', 'rejected', 'suspended']);

export type VenueStatus = z.infer<typeof VenueStatusEnum>;

/**
 * Change venue status schema (State Machine Pattern)
 * Used by: PUT /venues/:id/status
 */
export const ChangeVenueStatusSchema = z
  .object({
    status: VenueStatusEnum,
    reason: z.string().optional(),
  })
  .refine(
    (data) => {
      // Reason required for rejection and suspension
      if (['rejected', 'suspended'].includes(data.status)) {
        return !!data.reason && data.reason.length >= 10;
      }
      return true;
    },
    {
      message: 'Reason (minimum 10 characters) required for rejection/suspension',
      path: ['reason'],
    }
  );

export type ChangeVenueStatusRequest = z.infer<typeof ChangeVenueStatusSchema>;

/**
 * Venue response schema
 */
export const VenueResponseSchema = z.object({
  id: z.string(),
  vendorId: z.string(),
  name: z.string(),
  slug: z.string(),
  description: z.string(),
  venueType: z.string(),
  venueTypes: z.array(z.string()),
  cityId: z.string(),
  stateId: z.string(),
  address: z.string(),
  locality: z.string().nullish(),
  landmark: z.string().nullish(),
  pincode: z.string(),
  latitude: z.number(),
  longitude: z.number(),
  googleMapsLink: z.string().nullish(),
  coverImage: z.string().nullish(),
  images: z.array(z.string()),
  videos: z.array(z.string()),
  amenities: AmenitiesSchema,
  basePrice: z.number(),
  currency: z.string(),
  maxCapacity: z.number().nullish(),
  minPrice: z.number().nullish(), // Minimum hourly price from venue inventory (for "Starting from ₹X/hr")
  operatingHours: OperatingHoursOutputSchema.nullable(),
  phoneNumber: z.string().nullish(),
  whatsappNumber: z.string().nullish(),
  email: z.string().nullish(),
  isActive: z.boolean(),
  isApproved: z.boolean(),
  isSuspended: z.boolean().optional(),
  approvedAt: z.string().nullish(),
  approvedBy: z.string().nullish(),
  averageRating: z.number().nullish(),
  totalReviews: z.number(),
  totalBookings: z.number(),
  popularityScore: z.number().nullish(),
  seoUrl: z.string().nullish(),
  createdAt: z.string(),
  updatedAt: z.string(),
  vendor: z
    .object({
      id: z.string(),
      businessName: z.string(),
      isVerified: z.boolean(),
      instagram: z.string().nullish(),
    })
    .nullish(),
  city: z
    .object({
      id: z.string(),
      name: z.string(),
      slug: z.string(),
    })
    .nullish(),
  state: z
    .object({
      id: z.string(),
      name: z.string(),
      code: z.string(),
      slug: z.string().nullish(),
    })
    .nullish(),
  inventory: z
    .array(
      z.object({
        id: z.string(),
        inventoryItemId: z.string(),
      })
    )
    .optional(),
  cancellationPolicy: z.unknown().nullish(),
  hasFoodMenu: z.boolean().optional(),
});
export type VenueResponse = z.infer<typeof VenueResponseSchema>;

/**
 * Paginated venues response schema
 * @deprecated Use createPaginatedSchema(VenueResponseSchema) instead
 * This schema doesn't match the actual API envelope structure which includes success, data, and meta fields.
 * The backend ResponseTransformInterceptor wraps responses in { success: true, data: T[], meta: { pagination } }
 */
export const PaginatedVenuesResponseSchema = z.object({
  data: z.array(VenueResponseSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  totalPages: z.number(),
});
export type PaginatedVenuesResponse = z.infer<typeof PaginatedVenuesResponseSchema>;

// === VENDOR PORTAL SCHEMAS (VENU-03 to VENU-06) ===

/**
 * Venue update schema for vendor portal (VENU-03)
 * Vendor can update their venue information
 */
export const UpdateVenueSchemaForVendor = z.object({
  name: z.string().min(1, 'Venue name required').max(200).optional(),
  description: z.string().max(2000).optional(),
  address: z
    .object({
      line1: z.string().min(1, 'Address required').max(200),
      line2: z.string().max(200).optional(),
      // Use system city/state IDs from master data dropdowns
      cityId: z.string().min(1, 'City required'),
      stateId: z.string().min(1, 'State required'),
      pincode: z.string().regex(/^\d{6}$/, 'Invalid pincode'),
      latitude: z.number().min(-90).max(90).optional(),
      longitude: z.number().min(-180).max(180).optional(),
    })
    .optional(),
  amenities: z.array(z.string()).default([]).optional(),
  operatingHours: OperatingHoursSchema.optional(),
  contactPhone: z
    .string()
    .regex(/^\+?[1-9]\d{9,14}$/, 'Invalid phone')
    .optional()
    .or(z.literal('')),
  contactEmail: z.string().email().optional().or(z.literal('')),
  // Controls whether phone/email are shown on the customer-facing venue page
  isContactPublic: z.boolean().optional(),
});
export type UpdateVenueForVendor = z.infer<typeof UpdateVenueSchemaForVendor>;

/**
 * Venue photo upload schema (VENU-04)
 * Client-side validation for photo uploads
 */
export const VenuePhotoSchema = z.object({
  file: z
    .instanceof(File)
    .refine((f) => f.size <= 5 * 1024 * 1024, 'Photo must be under 5MB')
    .refine(
      (f) => ['image/jpeg', 'image/png', 'image/webp'].includes(f.type),
      'Only JPEG, PNG, or WebP allowed'
    ),
});

/**
 * Photo upload constraints for documentation
 */
export const VENUE_PHOTO_CONSTRAINTS = {
  maxSizeMB: 5,
  allowedTypes: ['image/jpeg', 'image/png', 'image/webp'],
  maxPhotos: 10,
} as const;

/**
 * Venue activation toggle schema (VENU-06)
 */
export const VenueActivationSchema = z.object({
  isActive: z.boolean(),
});
export type VenueActivation = z.infer<typeof VenueActivationSchema>;

// ============================================
// CANCELLATION POLICY SCHEMAS
// ============================================

/**
 * A single cancellation rule: refund percentage for cancellations X hours before booking
 */
export const CancellationRuleSchema = z.object({
  hoursBeforeBooking: z.number().min(0, 'Must be 0 or more hours'),
  refundPercentage: z.number().min(0).max(100, 'Must be 0-100%'),
});

export type CancellationRule = z.infer<typeof CancellationRuleSchema>;

/**
 * Vendor-configurable cancellation policy schema
 * Requires at least one rule and a 0-hour catch-all
 */
export const VendorCancellationPolicySchema = z.object({
  rules: z
    .array(CancellationRuleSchema)
    .min(1, 'At least one cancellation rule is required')
    .max(10, 'Maximum 10 rules allowed')
    .refine(
      (rules) => rules.some((r) => r.hoursBeforeBooking === 0),
      'Must include a catch-all rule for 0 hours (last-minute cancellations)'
    )
    .refine((rules) => {
      const hours = rules.map((r) => r.hoursBeforeBooking);
      return new Set(hours).size === hours.length;
    }, 'Each rule must have a unique hours-before-booking value'),
});

export type VendorCancellationPolicy = z.infer<typeof VendorCancellationPolicySchema>;
